/**
 * Parse legacy date formats and normalize to YYYY-MM-DD
 * - Year-only dates (e.g., "2017") are converted to:
 *   - Start dates: "2017-01-01" (January 1st)
 *   - End dates: "2017-12-31" (December 31st)
 * - Full dates (e.g., "2017-05-15") are returned as-is
 * - Empty/null values return empty string
 */
export const parseDate = (dateValue, isEndDate = false) => {
  if (!dateValue) {
    return ''
  }

  // Handle non-string values
  const dateStr = String(dateValue).trim()
  if (!dateStr) {
    return ''
  }

  // Already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr
  }

  // ISO 8601 timestamp format (e.g., "2021-03-01T00:00:00.000000Z")
  if (/^\d{4}-\d{2}-\d{2}T/.test(dateStr)) {
    return dateStr.split('T')[0]
  }

  // YYYY/MM/DD format (sometimes returned by APIs)
  if (/^\d{4}\/\d{2}\/\d{2}$/.test(dateStr)) {
    return dateStr.replace(/\//g, '-')
  }

  // YYYY-MM format (partial date)
  if (/^\d{4}-\d{2}$/.test(dateStr)) {
    if (isEndDate) {
      // For end dates, use last day of month
      const [year, month] = dateStr.split('-')
      const lastDayOfMonth = new Date(parseInt(year), parseInt(month), 0).getDate()
      return `${year}-${month}-${String(lastDayOfMonth).padStart(2, '0')}`
    } else {
      // For start dates, use first day of month
      return `${dateStr}-01`
    }
  }

  // Year-only format (e.g., "2017", "2022")
  if (/^\d{4}$/.test(dateStr)) {
    const year = dateStr
    if (isEndDate) {
      return `${year}-12-31` // December 31st for end dates
    } else {
      return `${year}-01-01` // January 1st for start dates
    }
  }

  // Other formats - return as-is and let browser handle it
  return dateStr
}

/**
 * Batch parse dates in an array of objects
 * Applies parseDate to startDate/start_date and endDate/end_date fields
 * Handles both camelCase and snake_case field names
 */
export const parseDateFields = (items) => {
  if (!Array.isArray(items)) {
    return []
  }

  return items.map(item => {
    // Get startDate from either camelCase or snake_case
    const startDateValue = item.startDate || item.start_date
    const endDateValue = item.endDate || item.end_date

    const parsed = {
      ...item,
      startDate: parseDate(startDateValue, false),
      endDate: parseDate(endDateValue, true)
    }

    // Also update snake_case versions if they exist
    if (item.start_date !== undefined) {
      parsed.start_date = parsed.startDate
    }
    if (item.end_date !== undefined) {
      parsed.end_date = parsed.endDate
    }

    return parsed
  })
}

/**
 * Check if a date is "recent" (within threshold days from today)
 * Used by activity timeline to prevent old items from appearing as new activity
 *
 * @param {string|Date|null} itemDate - The date to check
 * @param {number} thresholdDays - How many days back counts as "recent" (default: 14)
 * @returns {boolean} True if item is recent or date is ambiguous
 */
export const isRecentDate = (itemDate, thresholdDays = 14) => {
  // Null or empty = treat as recent (safer than filtering out)
  if (!itemDate) {
    return true
  }

  try {
    // Parse the date
    let date
    if (itemDate instanceof Date) {
      date = itemDate
    } else if (typeof itemDate === 'string') {
      date = new Date(itemDate)
    } else {
      return true // Unknown format, treat as recent
    }

    if (isNaN(date.getTime())) {
      return true // Invalid date, treat as recent
    }

    // Calculate threshold date
    const today = new Date()
    const thresholdDate = new Date(today.getTime() - thresholdDays * 24 * 60 * 60 * 1000)

    // Check if date is after threshold
    return date >= thresholdDate
  } catch (error) {
    console.warn('[DATE] Could not parse item date, treating as recent', { itemDate, error })
    return true
  }
}

/**
 * Check if any items in an array have recent dates
 * Used to determine if an activity should be displayed
 *
 * @param {array} items - Array of items (publications, grants, projects, etc.)
 * @param {string} dateField - Which field contains the date (e.g., 'date', 'start_date')
 * @param {number} thresholdDays - How many days back counts as "recent" (default: 14)
 * @returns {boolean} True if at least one item is recent
 */
export const hasRecentItems = (items, dateField = 'date', thresholdDays = 14) => {
  if (!Array.isArray(items) || items.length === 0) {
    return false
  }

  return items.some(item => {
    const itemDate = item?.[dateField] || null
    return isRecentDate(itemDate, thresholdDays)
  })
}
